import React, { useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Loader2, Play } from "lucide-react";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { VoiceSelectionModal } from "@/components/voice-selection-modal";

interface Character {
  name: string;
  selectedVoice: string;
  pronouns?: {
    subject: string;    // e.g., he/she/they
    object: string;     // e.g., him/her/them
    possessive: string; // e.g., his/her/their
  };
}

interface VoiceOption {
  id: string;
  name: string;
  gender: 'male' | 'female' | 'neutral';
  preview_url?: string;
}

interface CharacterVoiceSelectorProps {
  characters: Character[];
  onVoiceChange: (characterName: string, voiceId: string) => void;
  onPreviewVoice: (previewUrl: string) => void;
}

export function CharacterVoiceSelector({
  characters,
  onVoiceChange,
  onPreviewVoice
}: CharacterVoiceSelectorProps) {
  const [voices, setVoices] = useState<Record<string, VoiceOption[]>>({
    male: [],
    female: [],
    neutral: []
  });
  const [loading, setLoading] = useState(true);
  const [detectingGenders, setDetectingGenders] = useState(false);
  const [assigningVoices, setAssigningVoices] = useState(false);
  const [characterGenders, setCharacterGenders] = useState<Record<string, string>>({});
  const [voicesAssigned, setVoicesAssigned] = useState(false);
  const { toast } = useToast();
  
  // Load available voices from ElevenLabs
  useEffect(() => {
    const fetchVoices = async () => {
      try {
        const response = await apiRequest("GET", "/api/voices");
        const data = await response.json();
        
        const maleVoices = data.filter((v: any) => v.labels?.gender === 'male');
        const femaleVoices = data.filter((v: any) => v.labels?.gender === 'female');
        const neutralVoices = data.filter((v: any) => 
          !v.labels?.gender || v.labels?.gender === 'non-binary'
        );
        
        setVoices({
          male: maleVoices.map((v: any) => ({
            id: v.voice_id,
            name: v.name,
            gender: 'male',
            preview_url: v.preview_url
          })),
          female: femaleVoices.map((v: any) => ({
            id: v.voice_id,
            name: v.name,
            gender: 'female',
            preview_url: v.preview_url
          })),
          neutral: neutralVoices.map((v: any) => ({
            id: v.voice_id,
            name: v.name,
            gender: 'neutral',
            preview_url: v.preview_url
          }))
        });
        
        setLoading(false);
      } catch (error) {
        console.error("Failed to load voices:", error);
        toast({
          title: "Error Loading Voices",
          description: "We couldn't load the voice options. Please try again later.",
          variant: "destructive"
        });
        setLoading(false);
      }
    };
    
    fetchVoices();
  }, []);
  
  // Auto-assign voices based on character pronouns when voices are loaded
  useEffect(() => {
    if (!loading && !voicesAssigned && characters.length > 0 && 
        voices.male.length > 0 && voices.female.length > 0) {
      // Track used voices to avoid duplicates unless necessary
      const usedMaleVoices = new Set<string>();
      const usedFemaleVoices = new Set<string>();
      
      // First loop to count how many of each gender we need
      const maleCharacters = characters.filter(character => {
        // Check if character object has embedded pronouns data
        const characterObject = (character as any).pronouns;
        return characterObject?.subject === 'he';
      });
      
      const femaleCharacters = characters.filter(character => {
        // Check if character object has embedded pronouns data
        const characterObject = (character as any).pronouns;
        return characterObject?.subject === 'she';
      });
      
      // Second loop to assign voices
      characters.forEach((character) => {
        let selectedVoice;
        
        // Special handling for NARRATOR - always use George voice (the preferred one)
        if (character.name === 'NARRATOR') {
          const georgeVoice = voices.male.find(v => v.id === 'Yko7PKHZNXotIFUBG7I9') || 
                             voices.male.find(v => v.name === 'George');
          if (georgeVoice) {
            selectedVoice = georgeVoice;
            usedMaleVoices.add(georgeVoice.id);
          }
        } else {
          // Skip if already has a voice assigned for non-narrator characters
          if (character.selectedVoice) return;
          
          // Check if character object has embedded pronouns data
          const characterObject = (character as any).pronouns;
          const isFemale = characterObject?.subject === 'she';
          const isMale = !isFemale; // Default to male if not female
          
          if (isFemale && voices.female.length > 0) {
            // Find an unused female voice if possible
            const availableVoice = voices.female.find(v => !usedFemaleVoices.has(v.id));
            
            if (availableVoice || voices.female.length <= femaleCharacters.length) {
              // If we found an unused voice, or all voices will be used anyway due to count
              selectedVoice = availableVoice || voices.female[usedFemaleVoices.size % voices.female.length];
              usedFemaleVoices.add(selectedVoice.id);
            } else {
              // Otherwise use first female voice
              selectedVoice = voices.female[0];
              usedFemaleVoices.add(selectedVoice.id);
            }
          } else if (voices.male.length > 0) {
            // Find an unused male voice if possible, excluding George if already used for NARRATOR
            const availableVoice = voices.male.find(v => !usedMaleVoices.has(v.id));
            
            if (availableVoice || voices.male.length <= maleCharacters.length) {
              // If we found an unused voice, or all voices will be used anyway due to count
              selectedVoice = availableVoice || voices.male[usedMaleVoices.size % voices.male.length];
              usedMaleVoices.add(selectedVoice.id);
            } else {
              // Otherwise use first male voice
              selectedVoice = voices.male[0];
              usedMaleVoices.add(selectedVoice.id);
            }
          }
        }
        
        if (selectedVoice) {
          onVoiceChange(character.name, selectedVoice.id);
        }
      });
      
      setVoicesAssigned(true);
    }
  }, [characters, loading, voices, voicesAssigned, onVoiceChange]);
  
  // Detect character genders
  const detectGenders = async () => {
    if (!characters.length) return;
    
    setDetectingGenders(true);
    try {
      const characterNames = characters.map(c => c.name);
      const response = await apiRequest("POST", "/api/characters/detect-genders", { characterNames });
      const genders = await response.json();
      
      setCharacterGenders(genders);
      setDetectingGenders(false);
    } catch (error) {
      console.error("Failed to detect character genders:", error);
      toast({
        title: "Error Detecting Genders",
        description: "We couldn't determine the genders for your characters. Please assign voices manually.",
        variant: "destructive"
      });
      setDetectingGenders(false);
    }
  };
  
  // Auto-assign voices based on detected genders
  const autoAssignVoices = async () => {
    if (Object.keys(characterGenders).length === 0) {
      await detectGenders();
    }
    
    setAssigningVoices(true);
    try {
      const response = await apiRequest("POST", "/api/characters/assign-voices", { characterGenders });
      const voiceAssignments = await response.json();
      
      // Update each character with their assigned voice
      Object.entries(voiceAssignments).forEach(([characterName, voiceInfo]: [string, any]) => {
        onVoiceChange(characterName, voiceInfo.voice_id);
      });
      
      toast({
        title: "Voices Assigned",
        description: "Voice profiles have been automatically assigned to your characters.",
      });
      
      setAssigningVoices(false);
    } catch (error) {
      console.error("Failed to assign voices:", error);
      toast({
        title: "Error Assigning Voices",
        description: "We couldn't automatically assign voices. Please try assigning them manually.",
        variant: "destructive"
      });
      setAssigningVoices(false);
    }
  };
  
  // Handle voice change for a character
  const handleVoiceChange = (characterName: string, voiceId: string) => {
    onVoiceChange(characterName, voiceId);
  };
  
  // Get all available voice options for character selection
  const getVoicesForCharacter = (characterName: string) => {
    // Return all voices so users can choose from the complete selection
    return [...voices.male, ...voices.female, ...voices.neutral];
  };
  
  // Find voice by ID across all categories
  const findVoiceById = (voiceId: string): VoiceOption | undefined => {
    return [...voices.male, ...voices.female, ...voices.neutral].find(v => v.id === voiceId);
  };
  
  // Handle preview voice
  const handlePreviewVoice = (voiceId: string) => {
    const voice = findVoiceById(voiceId);
    if (voice && voice.preview_url) {
      console.log(`Playing preview for voice: ${voice.name}, URL: ${voice.preview_url}`);
      onPreviewVoice(voice.preview_url);
    } else {
      console.warn(`No preview URL available for voice ID: ${voiceId}`);
    }
  };
  
  if (loading) {
    return (
      <Card className="mb-6">
        <CardContent className="pt-6">
          <div className="flex flex-col items-center justify-center p-4">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <p className="mt-2 text-sm text-muted-foreground">Loading voice options...</p>
          </div>
        </CardContent>
      </Card>
    );
  }
  
  return (
    <Card className="mb-6">
      <CardContent className="pt-6">
        <div className="flex flex-col space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold">Character Voices</h3>
          </div>
          
          <Separator />
          
          {characters.length === 0 ? (
            <p className="text-center text-muted-foreground py-4">
              No characters found in this screenplay. Please upload a script with character dialogue.
            </p>
          ) : (
            <div className="grid gap-4">
              {[...characters]
                .sort((a, b) => {
                  // Put NARRATOR first
                  if (a.name === 'NARRATOR') return -1;
                  if (b.name === 'NARRATOR') return 1;
                  // Keep other characters in original order
                  return 0;
                })
                .map((character) => (
                <div key={character.name} className="grid grid-cols-12 items-center gap-4">
                  <div className="col-span-3">
                    <Label htmlFor={`voice-${character.name}`} className="font-medium">
                      {character.name}
                    </Label>
                  </div>
                  
                  <div className="col-span-7">
                    <VoiceSelectionModal
                      voices={getVoicesForCharacter(character.name)}
                      selectedVoiceId={character.selectedVoice}
                      onVoiceSelect={(voiceId) => handleVoiceChange(character.name, voiceId)}
                      characterName={character.name}
                    />
                  </div>
                  
                  <div className="col-span-2 flex justify-end">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => handlePreviewVoice(character.selectedVoice)}
                      disabled={!character.selectedVoice}
                    >
                      <Play className="h-4 w-4" />
                      <span className="sr-only">Preview</span>
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}